import currency from 'currency.js';
import { LEMNISCATE } from '../constants/textSymbols';
import { fractionalValues as values } from '../constants/fractionalValues';
import { BettingOptionsFormat } from '../enums/BettingOptionsFormat';
import formatAmount from './formatAmount';
import formatToCurrency from './formatToCurrency';
import roundValue from './roundValue';

interface IFormatBettingOption {
    (value: number): string;
}

interface IParams {
    value: number;
    format: BettingOptionsFormat;
    casinoConfig?: {
        currency?: {
            precision: number;
            template: string;
            code: string;
        };
        text: string;
        amount?: number;
        winAmountWithTaxes?: number;
    };
}

const fractionalValues: Record<string, string> = values;

const toFraction: IFormatBettingOption = (value: number): string => {
    let numerator1 = 1;
    let numerator2 = 0;
    let denominator1 = 0;
    let denominator2 = 1;
    let fraction = 1 / value;

    do {
        fraction = 1 / fraction;
        const coefficient = Math.floor(fraction);
        let auxilary = numerator1;
        numerator1 = coefficient * numerator1 + numerator2;
        numerator2 = auxilary;
        auxilary = denominator1;
        denominator1 = coefficient * denominator1 + denominator2;
        denominator2 = auxilary;
        fraction -= coefficient;
    } while (Math.abs(value - numerator1 / denominator1) > value * 1e-6);

    return `${numerator1}/${denominator1}`;
};

const formatDecimal: IFormatBettingOption = (value: number): string => {
    if (!/\d{5}/.test(String(value))) {
        return formatAmount(value, 2);
    }
    return formatAmount(value, 2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const formatAmerican: IFormatBettingOption = (value: number): string => {
    if (value < 1) {
        return '0';
    }

    if (value === 1) {
        return `-${LEMNISCATE}`;
    }

    if (value >= 2) {
        return `+${Math.round(100 * (value - 1)).toString()}`;
    }

    return Math.round(-100 / (value - 1)).toString();
};

const formatFractional: IFormatBettingOption = (value: number): string => {
    if (value < 1) {
        return '0';
    }

    if (typeof fractionalValues[value] !== 'undefined') {
        return fractionalValues[value];
    }

    return toFraction(roundValue(value - 1, 2));
};

const formatHongkong: IFormatBettingOption = (value: number): string => formatAmount(value - 1, 2);

const formatCasino = (value: number, config: IParams['casinoConfig']): string => {
    const amount = config.amount || 1;
    const formattedStake = formatToCurrency(
        amount,
        config.currency.precision,
        config.currency.template,
        config.currency.code,
        true,
    );
    const formattedValue = formatToCurrency(
        config.winAmountWithTaxes || formatDecimal(currency(amount).multiply(value).value),
        config.currency.precision,
        config.currency.template,
        config.currency.code,
    );

    return `${formattedStake} ${config.text} ${formattedValue}`;
};

const formatBettingOption = (params: IParams): string => {
    switch (params.format) {
        case BettingOptionsFormat.AMERICAN:
            return formatAmerican(params.value);
        case BettingOptionsFormat.FRACTIONAL:
            return formatFractional(params.value);
        case BettingOptionsFormat.HONGKONG:
            return formatHongkong(params.value);
        case BettingOptionsFormat.CASINO:
            return formatCasino(params.value, params.casinoConfig);
        default:
            return formatDecimal(params.value);
    }
};

export default formatBettingOption;
